import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, RefreshCw, Upload } from "lucide-react";
import { formatMoney } from "@finora/config";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";
import type { PurchaseInvoiceItem } from "@finora/api-client";

type Filter = string;
type Period = "this-month" | "last-month" | "this-year" | "custom";

const PERIODS: { key: Period; label: string }[] = [
  { key: "this-month", label: "Ovaj mesec" },
  { key: "last-month", label: "Prethodni mesec" },
  { key: "this-year", label: "Cela godina" },
  { key: "custom", label: "Od – do" },
];

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function periodRange(p: Period, from: string, to: string): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  if (p === "this-month") return { from: iso(new Date(y, m, 1)), to: iso(now) };
  if (p === "last-month") return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
  if (p === "this-year") return { from: iso(new Date(y, 0, 1)), to: iso(now) };
  return { from, to };
}

/** Seče period na delove od najviše 14 dana (jedan poziv ka serveru = jedan deo). */
function chunks(from: string, to: string): { from: string; to: string }[] {
  const out: { from: string; to: string }[] = [];
  const end = new Date(`${to}T00:00:00`);
  let cur = new Date(`${from}T00:00:00`);
  while (cur <= end) {
    const next = new Date(cur);
    next.setDate(next.getDate() + 13);
    const stop = next > end ? end : next;
    out.push({ from: iso(cur), to: iso(stop) });
    cur = new Date(stop);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "Sve" },
  { key: "New", label: "Nova" },
  { key: "Seen", label: "Viđena" },
  { key: "Approved", label: "Odobrena" },
  { key: "Rejected", label: "Odbijena" },
  { key: "Cancelled", label: "Otkazana" },
  { key: "Storno", label: "Stornirana" },
];

const STATUS_STYLE: Record<string, string> = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  seen: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-300 font-semibold",
  cancelled: "bg-slate-200 text-slate-700 border-slate-300",
  storno: "bg-purple-100 text-purple-800 border-purple-200",
};

/** Ključ statusa: odluka doneta kod nas ima prednost nad statusom sa SEF-a. */
function statusKey(r: { status: string | null; decision: string | null }): string {
  if (r.decision === "ACCEPTED") return "approved";
  if (r.decision === "REJECTED") return "rejected";
  return (r.status ?? "").toLowerCase() || "other";
}

/** Odluka (odobri/odbij) je moguća samo dok faktura čeka odluku. */
function isOpen(r: { source: string; status: string | null; decision: string | null }): boolean {
  return r.source === "SEF" && !r.decision && (!r.status || /^(new|seen)$/i.test(r.status));
}

const STATUS_LABEL: Record<string, string> = {
  new: "Nova",
  seen: "Viđena",
  approved: "Odobrena",
  rejected: "Odbijena",
  cancelled: "Otkazana",
  storno: "Stornirana",
};

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : "Greška";
}

/** Ulazne fakture sa SEF-a + označavanje kao trošak (ulazi u evidenciju troškova). */
export function PurchaseInvoicesTab() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["purchase-invoices", q, filter],
    queryFn: () => api.listPurchaseInvoices({ q, status: filter === "all" ? "" : filter }),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["purchase-invoices"] });
    qc.invalidateQueries({ queryKey: ["purchase-invoice-counts"] });
    qc.invalidateQueries({ queryKey: ["expenses"] });
  };

  const [period, setPeriod] = useState<Period>("this-month");
  const [customFrom, setCustomFrom] = useState(iso(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [customTo, setCustomTo] = useState(iso(new Date()));
  const [progress, setProgress] = useState<string | null>(null);

  const sync = useMutation({
    mutationFn: async () => {
      const r = periodRange(period, customFrom, customTo);
      if (!r.from || !r.to) throw new Error("Izaberi datume.");
      // SEF ne dozvoljava datum veći od juče
      const yest = new Date();
      yest.setDate(yest.getDate() - 1);
      const last = iso(yest);
      const toCapped = r.to > last ? last : r.to;
      if (r.from > toCapped) throw new Error("Za izabrani period SEF još nema podataka (najkasniji datum je juče).");
      const parts = chunks(r.from, toCapped);
      if (parts.length > 40) throw new Error("Period je predug (najviše oko godinu i po dana).");
      let imported = 0;
      let failed = 0;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]!;
        setProgress(`Povlačim ${i + 1}/${parts.length} (${part.from} – ${part.to})…`);
        // jedan deo se ponavlja dok server ne javi da nema više za preuzimanje
        for (let guard = 0; guard < 20; guard++) {
          const res = await api.syncPurchaseInvoices(part);
          imported += res.imported;
          failed += res.failed;
          if (!res.remaining || res.imported === 0) break;
        }
      }
      return { imported, failed };
    },
    onSuccess: ({ imported, failed }) => {
      setProgress(null);
      setMsg({
        ok: failed === 0,
        text: `Povučeno novih faktura: ${imported}.${failed ? ` Nije uspelo: ${failed} (detalji u Admin → Logovi → SEF).` : ""}`,
      });
      refresh();
    },
    onError: (e) => {
      setProgress(null);
      setMsg({ ok: false, text: errMsg(e) });
      refresh();
    },
  });

  const importXml = useMutation({
    mutationFn: (xml: string) => api.importPurchaseInvoiceXml(xml),
    onSuccess: () => {
      setMsg({ ok: true, text: "XML faktura je učitana." });
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });

  const mark = useMutation({
    mutationFn: async (ids: string[]) => {
      let done = 0;
      const errors: string[] = [];
      for (const id of ids) {
        try {
          await api.markPurchaseInvoiceExpense(id);
          done++;
        } catch (e) {
          errors.push(errMsg(e));
        }
      }
      return { done, errors };
    },
    onSuccess: ({ done, errors }) => {
      setSelected(new Set());
      setMsg({
        ok: errors.length === 0,
        text: `Označeno kao trošak: ${done}.${errors.length ? " Greška: " + errors[0] : ""}`,
      });
      refresh();
    },
  });

  const unmark = useMutation({
    mutationFn: (id: string) => api.unmarkPurchaseInvoiceExpense(id),
    onSuccess: () => {
      setMsg({ ok: true, text: "Trošak je uklonjen." });
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });

  const [detailId, setDetailId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<PurchaseInvoiceItem | null>(null);
  const [reason, setReason] = useState("");
  const [always, setAlways] = useState(false);
  const [matchText, setMatchText] = useState("");
  const [onlySupplier, setOnlySupplier] = useState(false);
  const rejectDetail = useQuery({
    queryKey: ["purchase-invoice", rejecting?.id],
    queryFn: () => api.getPurchaseInvoice(rejecting!.id),
    enabled: !!rejecting,
  });
  const [showRules, setShowRules] = useState(false);
  const [rulePib, setRulePib] = useState("");
  const [ruleText, setRuleText] = useState("");
  const [ruleReason, setRuleReason] = useState("");

  const detail = useQuery({
    queryKey: ["purchase-invoice", detailId],
    queryFn: () => api.getPurchaseInvoice(detailId as string),
    enabled: !!detailId,
  });
  const settings = useQuery({ queryKey: ["purchase-invoice-settings"], queryFn: () => api.getPurchaseInvoiceSettings() });
  const toggleAuto = useMutation({
    mutationFn: (enabled: boolean) => api.setPurchaseInvoiceAutoExpense(enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["purchase-invoice-settings"] }),
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });
  const counts = useQuery({ queryKey: ["purchase-invoice-counts"], queryFn: () => api.purchaseInvoiceStatusCounts() });
  const rules = useQuery({ queryKey: ["sef-auto-reject-rules"], queryFn: () => api.listSefAutoRejectRules() });

  const decide = useMutation({
    mutationFn: (v: { id: string; accepted: boolean; comment?: string; alwaysReject?: boolean; matchText?: string; onlySupplier?: boolean }) =>
      api.decidePurchaseInvoice(v.id, {
        accepted: v.accepted,
        comment: v.comment,
        alwaysReject: v.alwaysReject,
        matchText: v.matchText,
        onlySupplier: v.onlySupplier,
      }),
    onSuccess: (_r, v) => {
      setMsg({ ok: true, text: v.accepted ? "Faktura je odobrena na SEF-u." : "Faktura je odbijena na SEF-u." });
      setRejecting(null);
      setReason("");
      setAlways(false);
      setMatchText("");
      setOnlySupplier(false);
      refresh();
      qc.invalidateQueries({ queryKey: ["sef-auto-reject-rules"] });
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });

  const saveRule = useMutation({
    mutationFn: () => api.saveSefAutoRejectRule({ supplierTaxId: rulePib || undefined, matchText: ruleText, reason: ruleReason }),
    onSuccess: () => {
      setRulePib("");
      setRuleText("");
      setRuleReason("");
      qc.invalidateQueries({ queryKey: ["sef-auto-reject-rules"] });
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });
  const applyRules = useMutation({
    mutationFn: () => api.applySefAutoRejectRules(),
    onSuccess: (r) => {
      setMsg({
        ok: true,
        text: r.matched === 0 ? "Nijedna postojeća faktura ne odgovara pravilima." : `Odbijeno postojećih faktura: ${r.rejected} od ${r.matched}.`,
      });
      refresh();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e) }),
  });
  const delRule = useMutation({
    mutationFn: (id: string) => api.deleteSefAutoRejectRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sef-auto-reject-rules"] }),
  });

  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState("");

  const bulkDecide = useMutation({
    mutationFn: async (v: { ids: string[]; accepted: boolean; comment?: string }) => {
      let done = 0;
      const errors: string[] = [];
      for (let i = 0; i < v.ids.length; i++) {
        setProgress(`${v.accepted ? "Odobravam" : "Odbijam"} ${i + 1}/${v.ids.length}…`);
        try {
          await api.decidePurchaseInvoice(v.ids[i]!, { accepted: v.accepted, comment: v.comment });
          done++;
        } catch (e) {
          errors.push(errMsg(e));
        }
      }
      return { done, errors, accepted: v.accepted };
    },
    onSuccess: ({ done, errors, accepted }) => {
      setProgress(null);
      setBulkRejectOpen(false);
      setBulkReason("");
      setSelected(new Set());
      setMsg({
        ok: errors.length === 0,
        text: `${accepted ? "Odobreno" : "Odbijeno"}: ${done}.${errors.length ? ` Nije uspelo: ${errors.length} (${errors[0]})` : ""}`,
      });
      refresh();
    },
    onError: (e) => {
      setProgress(null);
      setMsg({ ok: false, text: errMsg(e) });
    },
  });

  const rows = data ?? [];
  const selectable = rows;
  const allChecked = selectable.length > 0 && selectable.every((r) => selected.has(r.id));

  const toggle = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    importXml.mutate(await f.text());
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Pretraga: broj, dobavljač, PIB"
          className="w-72 rounded-lg border border-border px-3 py-2 text-sm"
        />
        <div className="ml-auto flex flex-wrap gap-2">
          <Button onClick={() => setShowRules((v) => !v)}>
            Automatsko odbijanje{rules.data?.length ? ` (${rules.data.length})` : ""}
          </Button>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value as Period)}
            className="rounded-lg border border-border px-2 py-2 text-sm"
            disabled={sync.isPending}
          >
            {PERIODS.map((p) => (
              <option key={p.key} value={p.key}>
                {p.label}
              </option>
            ))}
          </select>
          {period === "custom" && (
            <>
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm" />
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="rounded-lg border border-border px-2 py-2 text-sm" />
            </>
          )}
          <Button onClick={() => sync.mutate()} disabled={sync.isPending}>
            <RefreshCw className="mr-1.5 h-4 w-4" /> {sync.isPending ? "Povlačim..." : "Povuci iz SEF-a"}
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={importXml.isPending}>
            <Upload className="mr-1.5 h-4 w-4" /> Učitaj XML
          </Button>
          <input ref={fileRef} type="file" accept=".xml,text/xml" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => {
          const n = counts.data?.[f.key === "all" ? "all" : f.key.toLowerCase()] ?? 0;
          if (f.key !== "all" && n === 0 && filter !== f.key) return null;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-3 py-1 text-sm ${
                STATUS_STYLE[f.key.toLowerCase()] ?? "border-border bg-surface text-slate-700"
              } ${filter === f.key ? "ring-2 ring-slate-700 ring-offset-1" : "opacity-80 hover:opacity-100"}`}
            >
              {f.label} <span className="opacity-70">({n})</span>
            </button>
          );
        })}
      </div>

      <label className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={settings.data?.autoExpense ?? false}
          disabled={toggleAuto.isPending}
          onChange={(e) => toggleAuto.mutate(e.target.checked)}
        />
        Sve nove ulazne fakture automatski evidentiraj kao trošak (razvrstavanje po kontima radi knjigovođa u Minimaxu)
      </label>

      {progress && <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">{progress}</div>}

      {msg && (
        <div className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      {showRules && (
        <div className="space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
          <p className="text-sm text-slate-600">
            Nove fakture koje u nazivu ili opisu stavke (ili napomeni) sadrže zadati tekst se pri povlačenju sa SEF-a automatski odbijaju sa uvek istim razlogom.
            Tekst ne mora da bude ceo, npr. „Trgovačka naknada za neostvareni promet“ pogađa sve mesece.
          </p>
          {(rules.data ?? []).map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <div className="flex-1">
                <div className="font-medium text-slate-800">
                  {r.matchText ? `Tekst: „${r.matchText}“` : "Sve fakture dobavljača"}
                  {r.supplierTaxId && (
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      samo {r.supplierName ?? "dobavljač"} (PIB {r.supplierTaxId})
                    </span>
                  )}
                </div>
                <div className="text-slate-600">Razlog: {r.reason}</div>
              </div>
              <button className="text-sm text-slate-500 hover:text-red-600" onClick={() => delRule.mutate(r.id)}>
                Ukloni
              </button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <input value={ruleText} onChange={(e) => setRuleText(e.target.value)} placeholder="Tekst u fakturi (npr. Trgovačka naknada za neostvareni promet)" className="min-w-[18rem] flex-1 rounded-lg border border-border px-3 py-2 text-sm" />
            <input value={rulePib} onChange={(e) => setRulePib(e.target.value)} placeholder="PIB (nije obavezan)" className="w-40 rounded-lg border border-border px-3 py-2 text-sm" />
            <input value={ruleReason} onChange={(e) => setRuleReason(e.target.value)} placeholder="Razlog odbijanja" className="min-w-[14rem] flex-1 rounded-lg border border-border px-3 py-2 text-sm" />
            <Button onClick={() => saveRule.mutate()} disabled={(!ruleText.trim() && !rulePib) || ruleReason.trim().length < 3 || saveRule.isPending}>
              Dodaj pravilo
            </Button>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-400">Pravilo možeš da dodaš i pri odbijanju fakture: izaberi tekst sa fakture.</p>
            <Button onClick={() => applyRules.mutate()} disabled={applyRules.isPending || !(rules.data ?? []).length}>
              {applyRules.isPending ? "Primenjujem..." : "Primeni na postojeće fakture"}
            </Button>
          </div>
        </div>
      )}

      {rows.length > 0 && (() => {
        const sel = rows.filter((r) => selected.has(r.id));
        const decidable = sel.filter((r) => isOpen(r));
        const rejectable = decidable.filter((r) => !r.expenseId);
        const markable = sel.filter((r) => !r.expenseId && statusKey(r) !== "rejected");
        return (
          <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm">
            <label className="flex items-center gap-2 text-slate-700">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={() => setSelected(allChecked ? new Set() : new Set(selectable.map((r) => r.id)))}
              />
              Izaberi sve prikazane ({rows.length})
            </label>
            {sel.length > 0 && (
              <>
                <span className="text-slate-500">Izabrano: {sel.length}</span>
                <Button
                  disabled={decidable.length === 0 || bulkDecide.isPending}
                  onClick={() => {
                    if (window.confirm(`Odobriti ${decidable.length} faktura na SEF-u?`)) {
                      bulkDecide.mutate({ ids: decidable.map((r) => r.id), accepted: true });
                    }
                  }}
                >
                  Odobri sve ({decidable.length})
                </Button>
                <Button disabled={rejectable.length === 0 || bulkDecide.isPending} onClick={() => setBulkRejectOpen(true)}>
                  Odbij sve ({rejectable.length})
                </Button>
                <Button disabled={markable.length === 0 || mark.isPending} onClick={() => mark.mutate(markable.map((r) => r.id))}>
                  Označi kao trošak ({markable.length})
                </Button>
                <button className="text-slate-500 hover:text-slate-800" onClick={() => setSelected(new Set())}>
                  Poništi izbor
                </button>
              </>
            )}
          </div>
        );
      })()}

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-5">
            <EmptyState
              icon={FileText}
              title="Nema ulaznih faktura"
              description='Klikni "Osveži iz SEF-a" ili učitaj XML fakture.'
            />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={() => setSelected(allChecked ? new Set() : new Set(selectable.map((r) => r.id)))}
                  />
                </th>
                <th>Broj</th>
                <th>Dobavljač</th>
                <th>Šta je fakturisano</th>
                <th>Datum</th>
                <th>Iznos</th>
                <th>Status</th>
                <th>Trošak</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={`cursor-pointer ${statusKey(r) === "rejected" ? "bg-red-50 hover:bg-red-100" : "hover:bg-slate-50"}`} onClick={() => setDetailId(r.id)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="font-medium text-slate-800">{r.number}</td>
                  <td className={statusKey(r) === "rejected" ? "font-semibold text-red-700" : "text-slate-600"}>
                    {r.supplierName}
                    {r.supplierTaxId && <span className="ml-1 text-xs text-slate-400">PIB {r.supplierTaxId}</span>}
                  </td>
                  <td className="max-w-[320px] text-xs text-slate-500" title={r.itemsSummary ?? ""}>
                    <span className="line-clamp-2">{r.itemsSummary || "—"}</span>
                  </td>
                  <td className="text-slate-500">
                    {r.issueDate ? new Date(r.issueDate).toLocaleDateString("sr-RS") : "—"}
                  </td>
                  <td>{formatMoney(r.grossAmount, r.currency)}</td>
                  <td>
                    {(() => {
                      const k = statusKey(r);
                      const label = r.decision === "REJECTED"
                        ? `Odbijena${r.decidedAuto ? " (auto)" : ""}`
                        : k === "other" ? "—" : (STATUS_LABEL[k] ?? r.status ?? "—");
                      return (
                        <span
                          className={`rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[k] ?? "border-border bg-slate-50 text-slate-500"}`}
                          title={r.decisionComment ?? ""}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {r.expenseId ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Trošak
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="space-x-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    {isOpen(r) && (
                      <>
                        <button
                          className="text-sm font-medium text-emerald-700 hover:underline"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: r.id, accepted: true })}
                        >
                          Odobri
                        </button>
                        <button
                          className="text-sm font-medium text-red-600 hover:underline"
                          disabled={decide.isPending}
                          onClick={() => {
                            setRejecting(r);
                            setReason("");
                            setAlways(false);
                            setMatchText("");
                            setOnlySupplier(false);
                          }}
                        >
                          Odbij
                        </button>
                      </>
                    )}
                    {r.expenseId ? (
                      <button
                        className="text-sm text-slate-500 hover:text-red-600"
                        disabled={unmark.isPending}
                        onClick={() => unmark.mutate(r.id)}
                      >
                        Ukloni iz troškova
                      </button>
                    ) : (
                      <button
                        className="text-sm font-medium text-brand hover:underline"
                        disabled={mark.isPending}
                        onClick={() => mark.mutate([r.id])}
                      >
                        Označi kao trošak
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setRejecting(null)}>
          <div className="w-full max-w-md space-y-3 rounded-xl bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900">Odbij fakturu {rejecting.number}</h3>
            <p className="text-sm text-slate-500">{rejecting.supplierName}</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Razlog odbijanja (obavezan, vidi ga dobavljač)"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
            <div className="space-y-2 rounded-lg bg-slate-50 p-3">
              <div className="text-sm font-medium text-slate-700">Automatsko odbijanje (nije obavezno)</div>
              <p className="text-xs text-slate-500">Izaberi tekst sa fakture ili upiši svoj. Buduće fakture koje ga sadrže biće odbijene same.</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  ...(rejectDetail.data?.note ? [rejectDetail.data.note] : []),
                  ...Array.from(new Set((rejectDetail.data?.lines ?? []).flatMap((l) => [l.name, l.description ?? ""]).filter(Boolean))),
                ]
                  .slice(0, 8)
                  .map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setMatchText(t)}
                      className="max-w-full truncate rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-slate-600 hover:bg-slate-100"
                      title={t}
                    >
                      {t.length > 60 ? `${t.slice(0, 60)}…` : t}
                    </button>
                  ))}
              </div>
              <input
                value={matchText}
                onChange={(e) => {
                  setMatchText(e.target.value);
                  if (e.target.value.trim().length >= 4) setAlways(true);
                }}
                placeholder="Tekst koji se traži u fakturi (možeš da skratiš, npr. bez meseca)"
                className="w-full rounded-lg border border-border px-3 py-2 text-sm"
              />
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={always} disabled={matchText.trim().length < 4} onChange={(e) => setAlways(e.target.checked)} />
                Uvek automatski odbijaj fakture koje sadrže ovaj tekst
              </label>
              {rejecting.supplierTaxId && (
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={onlySupplier} onChange={(e) => setOnlySupplier(e.target.checked)} />
                  Samo za ovog dobavljača ({rejecting.supplierName})
                </label>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button onClick={() => setRejecting(null)}>Otkaži</Button>
              <Button
                disabled={reason.trim().length < 3 || decide.isPending}
                onClick={() => decide.mutate({ id: rejecting.id, accepted: false, comment: reason, alwaysReject: always && matchText.trim().length >= 4, matchText: matchText.trim(), onlySupplier })}
              >
                Odbij fakturu
              </Button>
            </div>
          </div>
        </div>
      )}

      {bulkRejectOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBulkRejectOpen(false)}>
          <div className="w-full max-w-md space-y-3 rounded-xl bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-900">Odbij izabrane fakture</h3>
            <p className="text-sm text-slate-500">Isti razlog se šalje za sve izabrane fakture koje čekaju odluku.</p>
            <textarea
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              rows={3}
              placeholder="Razlog odbijanja (obavezan, vidi ga dobavljač)"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm"
            />
            <div className="flex justify-end gap-2">
              <Button onClick={() => setBulkRejectOpen(false)}>Otkaži</Button>
              <Button
                disabled={bulkReason.trim().length < 3 || bulkDecide.isPending}
                onClick={() => {
                  const ids = rows.filter((r) => selected.has(r.id) && isOpen(r) && !r.expenseId).map((r) => r.id);
                  bulkDecide.mutate({ ids, accepted: false, comment: bulkReason });
                }}
              >
                Odbij sve
              </Button>
            </div>
          </div>
        </div>
      )}

      {detailId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDetailId(null)}>
          <div className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl bg-surface p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {detail.isLoading || !detail.data ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">Faktura {detail.data.number}</h3>
                    <p className="text-sm text-slate-600">
                      {detail.data.supplierName}
                      {detail.data.supplierTaxId && <span className="ml-1 text-xs text-slate-400">PIB {detail.data.supplierTaxId}</span>}
                    </p>
                    {detail.data.supplierAddress && <p className="text-xs text-slate-400">{detail.data.supplierAddress}</p>}
                  </div>
                  <button className="text-sm text-slate-500 hover:text-slate-800" onClick={() => setDetailId(null)}>
                    Zatvori
                  </button>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                  <span>Datum: {detail.data.issueDate ? new Date(detail.data.issueDate).toLocaleDateString("sr-RS") : "—"}</span>
                  <span>Valuta plaćanja: {detail.data.dueDate ? new Date(detail.data.dueDate).toLocaleDateString("sr-RS") : "—"}</span>
                  <span>Status: {detail.data.decision === "ACCEPTED" ? "Odobrena" : detail.data.decision === "REJECTED" ? "Odbijena" : (detail.data.status ?? "—")}</span>
                </div>
                {detail.data.decisionComment && (
                  <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">Razlog: {detail.data.decisionComment}</p>
                )}
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Naziv</th>
                      <th className="text-right">Količina</th>
                      <th className="text-right">Cena</th>
                      <th className="text-right">Rabat</th>
                      <th className="text-right">PDV</th>
                      <th className="text-right">Osnovica</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data.lines.map((l) => (
                      <tr key={l.lineNo}>
                        <td className="text-slate-400">{l.lineNo}</td>
                        <td className="text-slate-800">
                          {l.name}
                          {l.sellerCode && <span className="ml-1 text-xs text-slate-400">({l.sellerCode})</span>}
                        </td>
                        <td className="text-right">{l.quantity} {l.unit}</td>
                        <td className="text-right">{formatMoney(String(l.unitPrice), detail.data!.currency)}</td>
                        <td className="text-right">{l.discountPercent ? `${l.discountPercent}%` : "—"}</td>
                        <td className="text-right">{l.taxRatePercent}%</td>
                        <td className="text-right">{formatMoney(String(l.lineNet), detail.data!.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="ml-auto w-64 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Osnovica</span><span>{formatMoney(String(detail.data.netAmount), detail.data.currency)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">PDV</span><span>{formatMoney(String(detail.data.vatAmount), detail.data.currency)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Ukupno</span><span>{formatMoney(String(detail.data.grossAmount), detail.data.currency)}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
